import { useState } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from '../components/ui/dropdown-menu';
import { Sheet, SheetContent, SheetTrigger, SheetHeader, SheetTitle } from '../components/ui/sheet';
import { Globe, Menu, User, LogOut, Heart, MessageCircle, Search, LayoutDashboard, Crown, Edit, ShieldCheck } from 'lucide-react';
import { auth } from '../lib/firebase';
import { signOut } from 'firebase/auth';
import NotificationCenter from './NotificationCenter';

const languages = [
  { code: 'en', label: 'English', flag: 'EN' },
  { code: 'hi', label: 'हिन्दी', flag: 'हि' },
  { code: 'mr', label: 'मराठी', flag: 'मरा' },
];

export default function Navbar() {
  const { lang, setLanguage, t } = useLanguage();
  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleLogout = async () => {
    await signOut(auth);
    setUser(null);
    localStorage.removeItem('auth_token');
    navigate('/');
  };

  const navLinks = user ? [
    { to: '/dashboard', label: t('nav.dashboard'), icon: LayoutDashboard },
    { to: '/search', label: t('nav.search'), icon: Search },
    { to: '/chat', label: t('nav.chat'), icon: MessageCircle },
    { to: '/plans', label: t('nav.plans'), icon: Crown },
  ] : [
    { to: '/plans', label: t('nav.plans'), icon: Crown },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <nav className="sticky top-0 z-50 bg-white/95 backdrop-blur-md border-b border-[#E7E5E4] shadow-sm" data-testid="navbar">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          <Link to={user ? '/dashboard' : '/'} className="flex items-center gap-2 group" data-testid="logo-link">
            <Heart className="w-7 h-7 text-[#D9381E] fill-[#D9381E] group-hover:scale-110 transition-transform" />
            <span className="text-xl font-bold text-[#2D2424] tracking-tight">
              Vivah<span className="text-[#D9381E]">Rang</span>
            </span>
          </Link>

          <div className="hidden md:flex items-center gap-1">
            {navLinks.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                data-testid={`nav-${link.to.replace('/', '')}`}
                className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                  isActive(link.to)
                    ? 'bg-[#D9381E]/10 text-[#D9381E]'
                    : 'text-[#2D2424] hover:bg-[#F2A900]/10 hover:text-[#D9381E]'
                }`}
              >
                <link.icon className="w-4 h-4" />
                {link.label}
              </Link>
            ))}
          </div>

          <div className="flex items-center gap-2">
            {user && <NotificationCenter />}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" className="gap-1.5 text-[#2D2424]" data-testid="language-switcher">
                  <Globe className="w-4 h-4" />
                  <span className="text-xs font-semibold">{languages.find(l => l.code === lang)?.flag}</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {languages.map((l) => (
                  <DropdownMenuItem
                    key={l.code}
                    onClick={() => setLanguage(l.code)}
                    className={lang === l.code ? 'bg-[#D9381E]/10 text-[#D9381E]' : ''}
                    data-testid={`lang-${l.code}`}
                  >
                    <span className="font-semibold mr-2">{l.flag}</span>
                    {l.label}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            {user ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="sm" className="gap-1.5" data-testid="user-menu">
                    <div className="w-8 h-8 rounded-full bg-[#D9381E] flex items-center justify-center text-white text-sm font-bold">
                      {user.name?.[0]?.toUpperCase() || 'U'}
                    </div>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48">
                  <div className="px-2 py-1.5 text-sm font-medium text-[#2D2424] truncate">{user.name}</div>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => navigate('/dashboard')} data-testid="menu-dashboard">
                    <LayoutDashboard className="w-4 h-4 mr-2" /> {t('nav.dashboard')}
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/profile/edit')} data-testid="menu-edit-profile">
                    <Edit className="w-4 h-4 mr-2" /> {t('profile.edit')}
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/kyc')} data-testid="menu-kyc">
                    <ShieldCheck className="w-4 h-4 mr-2" /> KYC Verification
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/plans')} data-testid="menu-plans">
                    <Crown className="w-4 h-4 mr-2" /> {t('nav.plans')}
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogout} className="text-red-600" data-testid="menu-logout">
                    <LogOut className="w-4 h-4 mr-2" /> {t('nav.logout')}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <Button
                onClick={() => navigate('/auth')}
                className="bg-[#D9381E] hover:bg-[#B91C1C] text-white rounded-full px-5 h-9 text-sm font-semibold shadow-md hover:shadow-lg transition-all"
                data-testid="login-btn"
              >
                {t('nav.login')}
              </Button>
            )}

            <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="sm" className="md:hidden" data-testid="mobile-menu-btn">
                  <Menu className="w-5 h-5" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-72">
                <SheetHeader>
                  <SheetTitle className="flex items-center gap-2">
                    <Heart className="w-5 h-5 text-[#D9381E] fill-[#D9381E]" />
                    VivahRang
                  </SheetTitle>
                </SheetHeader>
                <div className="mt-6 flex flex-col gap-1">
                  {navLinks.map((link) => (
                    <button
                      key={link.to}
                      onClick={() => { navigate(link.to); setMobileOpen(false); }}
                      className={`flex items-center gap-3 px-3 py-3 rounded-lg text-sm font-medium transition-colors text-left ${
                        isActive(link.to) ? 'bg-[#D9381E]/10 text-[#D9381E]' : 'text-[#2D2424] hover:bg-[#F9F5F0]'
                      }`}
                    >
                      <link.icon className="w-5 h-5" />
                      {link.label}
                    </button>
                  ))}
                  {!user && (
                    <Button
                      onClick={() => { navigate('/auth'); setMobileOpen(false); }}
                      className="mt-4 bg-[#D9381E] text-white rounded-full"
                    >
                      {t('nav.login')}
                    </Button>
                  )}
                  {user && (
                    <button
                      onClick={() => { handleLogout(); setMobileOpen(false); }}
                      className="flex items-center gap-3 px-3 py-3 rounded-lg text-sm font-medium text-red-600 hover:bg-red-50 mt-4"
                    >
                      <LogOut className="w-5 h-5" />
                      {t('nav.logout')}
                    </button>
                  )}
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
    </nav>
  );
}
